import { Css, formatDate, FormLines, StaticField, useTestIds } from "@homebound/beam";
import { Card } from "src/components/Card";
import {
  ReadyPlanConfigCard_ReadyPlanConfigLikeFragment,
  ReadyPlanConfigCard_ReadyPlanOptionFragment,
  ReadyPlanConfigCard_StructureFragment,
} from "src/routes/projects/change-events/guided/components/ReadyPlanConfigCard.generated";

type ReadyPlanConfigCardProps = {
  structure: ReadyPlanConfigCard_StructureFragment;
  readyPlanConfig: ReadyPlanConfigCard_ReadyPlanConfigLikeFragment;
  options?: ReadyPlanConfigCard_ReadyPlanOptionFragment[];
  /** Whether the card shows its border + shadow. Defaults to true. */
  bordered?: boolean;
};

/** Displays a ready plan configuration (plan, version, updated). */
export function ReadyPlanConfigCard(props: ReadyPlanConfigCardProps) {
  const { structure, readyPlanConfig, options, bordered } = props;
  const tid = useTestIds({}, "readyPlanConfigCard");
  return (
    <Card bordered={bordered}>
      <div css={Css.df.fdr.gap3.$} {...tid}>
        <div css={Css.smSb.$}>{readyPlanConfig.name ?? ""}</div>
        <ReadyPlanConfigCardDetailContent readyPlanConfig={readyPlanConfig} structure={structure} options={options} />
      </div>
    </Card>
  );
}

type ReadyPlanConfigCardDetailContentProps = Pick<
  ReadyPlanConfigCardProps,
  "structure" | "readyPlanConfig" | "options"
>;
export function ReadyPlanConfigCardDetailContent(props: ReadyPlanConfigCardDetailContentProps) {
  const { readyPlanConfig, structure, options = [] } = props;
  const { updatedAt } = readyPlanConfig;
  const programData =
    structure.isAdu && readyPlanConfig.__typename === "CheckoutConfig"
      ? readyPlanConfig.aduProgramData
      : readyPlanConfig.programData;
  const optionsByGroup = options
    .filter((o) => o.optionGroup.forDesignPackages)
    .sortBy((o) => o.optionGroup.order)
    .groupBy(
      (o) => o.optionGroup.name,
      (o) => o.maybeShortName,
    );

  // Build a flat list of label/value pairs so we can split them evenly across two columns.
  const fields: { label: string; value: string | number | null | undefined }[] = [
    ...optionsByGroup.toEntries().map(([groupName, values]) => ({ label: groupName, value: values.join(", ") })),
    ...(programData
      ? [
          { label: "Sq Ft", value: programData.sellableSqft },
          { label: "Bedrooms", value: programData.bedrooms },
          { label: "Baths", value: bathCount(programData) },
          { label: "Width", value: programData.widthInFeet },
          { label: "Depth", value: programData.depthInFeet },
        ]
      : []),
    { label: "Updated", value: updatedAt ? formatDate(updatedAt.toPlainDate(), "shortDate") : "N/A" },
  ];
  const midpoint = Math.ceil(fields.length / 2);
  const columns = { left: fields.slice(0, midpoint), right: fields.slice(midpoint) };
  return (
    <div css={Css.df.fdr.gap5.$}>
      {columns.toEntries().map(([side, columnFields]) => (
        <FormLines key={side} width="sm" labelStyle="left" gap={0}>
          {columnFields.map((field) => (
            <StaticField key={field.label} label={field.label} value={field.value?.toString() ?? ""} />
          ))}
        </FormLines>
      ))}
    </div>
  );
}

/** Combines full and half baths into a single count (a half bath counts as 0.5). */
function bathCount(programData: { fullBaths?: number | null; halfBaths?: number | null }): number {
  return (programData.fullBaths ?? 0) + (programData.halfBaths ?? 0) * 0.5;
}
